// Branded HTML Studio — section field editors + design controls.
// Kept separate from the page so the orchestration file stays readable.
import { Trash2, Plus, GripVertical, ChevronUp, ChevronDown, Copy } from "lucide-react";

const SECTION_LABELS = {
  header: "Header", hero: "Hero", text: "Text", features: "Feature list",
  image: "Image", quote: "Quote", pricing: "Pricing", faq: "FAQ",
  cta: "Call to action", footer: "Footer",
};
export { SECTION_LABELS };

function Field({ label, value, onChange, area, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#64748B]">{label}</span>
      {area ? (
        <textarea
          data-testid={testid} value={value || ""} rows={3} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-[13.5px] text-[#0A2342] focus:outline-none focus:border-[#4FD1C5]"
        />
      ) : (
        <input
          data-testid={testid} value={value || ""} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-[13.5px] text-[#0A2342] focus:outline-none focus:border-[#4FD1C5]"
        />
      )}
    </label>
  );
}

function ItemList({ items, onChange, fields, addLabel }) {
  const set = (i, key, val) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    onChange(next);
  };
  const add = () => onChange([...(items || []), { _k: (crypto.randomUUID?.() || String(Math.random())), ...Object.fromEntries(fields.map((f) => [f.key, ""])) }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {(items || []).map((it, i) => (
        <div key={it._k || `${it[fields[0].key] || ""}-${i}`} className="rounded-md border border-[#E2E8F0] p-3 space-y-2 bg-[#F8FAFC]">
          <div className="flex justify-end">
            <button type="button" onClick={() => remove(i)} className="text-[#B91C1C] hover:text-[#7F1D1D]"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          {fields.map((f) => (
            <Field key={f.key} label={f.label} area={f.area} value={it[f.key]} onChange={(v) => set(i, f.key, v)} />
          ))}
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1D4E89] hover:text-[#0A2342]">
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </button>
    </div>
  );
}

export function SectionEditor({ section, onChange }) {
  const up = (patch) => onChange({ ...section, ...patch });
  const t = section.type;
  return (
    <div className="space-y-3">
      {t === "header" && (
        <>
          <label className="flex items-center gap-2 text-[13px] text-[#0A2342]">
            <input type="checkbox" checked={section.show_logo !== false} onChange={(e) => up({ show_logo: e.target.checked })} />
            Show brand logo
          </label>
          <Field label="Tagline" value={section.tagline} onChange={(v) => up({ tagline: v })} />
        </>
      )}
      {t === "hero" && (
        <>
          <Field label="Eyebrow" value={section.eyebrow} onChange={(v) => up({ eyebrow: v })} />
          <Field label="Heading" value={section.heading} onChange={(v) => up({ heading: v })} />
          <Field label="Subheading" area value={section.subheading} onChange={(v) => up({ subheading: v })} />
          <Field label="Button label" value={section.cta_label} onChange={(v) => up({ cta_label: v })} />
          <Field label="Button URL" value={section.cta_url} onChange={(v) => up({ cta_url: v })} placeholder="https://" />
          <Field label="Image URL (optional)" value={section.image_url} onChange={(v) => up({ image_url: v })} placeholder="https://" />
        </>
      )}
      {t === "text" && (
        <>
          <Field label="Heading (optional)" value={section.heading} onChange={(v) => up({ heading: v })} />
          <Field label="Body" area value={section.body} onChange={(v) => up({ body: v })} placeholder="Use a blank line between paragraphs" />
        </>
      )}
      {t === "features" && (
        <>
          <Field label="Heading (optional)" value={section.heading} onChange={(v) => up({ heading: v })} />
          <ItemList items={section.items} onChange={(v) => up({ items: v })} addLabel="Add feature"
            fields={[{ key: "title", label: "Title" }, { key: "body", label: "Body", area: true }]} />
        </>
      )}
      {t === "image" && (
        <>
          <Field label="Image URL" value={section.image_url} onChange={(v) => up({ image_url: v })} placeholder="https://" />
          <Field label="Alt text" value={section.alt} onChange={(v) => up({ alt: v })} />
          <Field label="Caption (optional)" value={section.caption} onChange={(v) => up({ caption: v })} />
        </>
      )}
      {t === "quote" && (
        <>
          <Field label="Quote" area value={section.quote} onChange={(v) => up({ quote: v })} />
          <Field label="Attribution" value={section.attribution} onChange={(v) => up({ attribution: v })} />
        </>
      )}
      {t === "pricing" && (
        <>
          <Field label="Heading (optional)" value={section.heading} onChange={(v) => up({ heading: v })} />
          <ItemList items={section.plans} onChange={(v) => up({ plans: v })} addLabel="Add plan"
            fields={[
              { key: "name", label: "Name" }, { key: "price", label: "Price" }, { key: "period", label: "Period (e.g. /mo)" },
              { key: "cta_label", label: "Button label" }, { key: "cta_url", label: "Button URL" },
            ]} />
          <p className="text-[11px] text-[#94A3B8]">Tip: edit plan features in code view for fine control.</p>
        </>
      )}
      {t === "faq" && (
        <>
          <Field label="Heading (optional)" value={section.heading} onChange={(v) => up({ heading: v })} />
          <ItemList items={section.items} onChange={(v) => up({ items: v })} addLabel="Add question"
            fields={[{ key: "q", label: "Question" }, { key: "a", label: "Answer", area: true }]} />
        </>
      )}
      {t === "cta" && (
        <>
          <Field label="Heading" value={section.heading} onChange={(v) => up({ heading: v })} />
          <Field label="Body" area value={section.body} onChange={(v) => up({ body: v })} />
          <Field label="Button label" value={section.cta_label} onChange={(v) => up({ cta_label: v })} />
          <Field label="Button URL" value={section.cta_url} onChange={(v) => up({ cta_url: v })} placeholder="https://" />
        </>
      )}
      {t === "footer" && (
        <>
          <Field label="Business name" value={section.business_name} onChange={(v) => up({ business_name: v })} />
          <Field label="Tagline" value={section.tagline} onChange={(v) => up({ tagline: v })} />
          <Field label="Contact (optional)" value={section.contact} onChange={(v) => up({ contact: v })} />
          <ItemList items={section.links} onChange={(v) => up({ links: v })} addLabel="Add link"
            fields={[{ key: "label", label: "Label" }, { key: "url", label: "URL" }]} />
        </>
      )}
    </div>
  );
}

export function SectionRow({ section, index, total, open, onToggle, onMove, onDelete, onDuplicate, onChange }) {
  return (
    <div className="rounded-md border border-[#E2E8F0] bg-white" data-testid={`section-row-${index}`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <GripVertical className="w-4 h-4 text-[#CBD5E1]" />
        <button type="button" onClick={onToggle} className="flex-1 text-left text-[13.5px] font-semibold text-[#0A2342]" data-testid={`section-toggle-${index}`}>
          {SECTION_LABELS[section.type] || section.type}
        </button>
        <button type="button" onClick={() => onMove(-1)} disabled={index === 0} className="text-[#64748B] disabled:opacity-30 hover:text-[#0A2342]" data-testid={`section-up-${index}`}><ChevronUp className="w-4 h-4" /></button>
        <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} className="text-[#64748B] disabled:opacity-30 hover:text-[#0A2342]" data-testid={`section-down-${index}`}><ChevronDown className="w-4 h-4" /></button>
        <button type="button" onClick={onDuplicate} className="text-[#64748B] hover:text-[#0A2342]" data-testid={`section-dup-${index}`}><Copy className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={onDelete} className="text-[#B91C1C] hover:text-[#7F1D1D]" data-testid={`section-del-${index}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      {open && (
        <div className="border-t border-[#E2E8F0] p-3">
          <SectionEditor section={section} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

function ColorRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex-1 text-[12.5px] text-[#334155]">{label}</span>
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-8 h-8 rounded border border-[#E2E8F0] p-0.5 cursor-pointer" data-testid={`${testid}-picker`} />
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-24 rounded-md border border-[#E2E8F0] px-2 py-1.5 text-[12px] font-mono text-[#0A2342]" data-testid={`${testid}-hex`} />
    </div>
  );
}

export function DesignPanel({ design, onChange, mode, emailFonts, webFonts, onResetBrand, onSaveToBrand }) {
  const set = (patch) => onChange({ ...design, ...patch });
  const fonts = mode === "email" ? emailFonts : webFonts;
  const fontUnsafe = (name) => mode === "email" && !emailFonts.includes(name);
  return (
    <div className="space-y-6" data-testid="design-panel">
      <div>
        <div className="dms-label mb-3">Colours</div>
        <div className="space-y-2">
          <ColorRow label="Primary" value={design.primary} onChange={(v) => set({ primary: v })} testid="color-primary" />
          <ColorRow label="Secondary" value={design.secondary} onChange={(v) => set({ secondary: v })} testid="color-secondary" />
          <ColorRow label="Accent" value={design.accent} onChange={(v) => set({ accent: v })} testid="color-accent" />
          <ColorRow label="Background" value={design.background} onChange={(v) => set({ background: v })} testid="color-bg" />
          <ColorRow label="Heading text" value={design.heading_color} onChange={(v) => set({ heading_color: v })} testid="color-heading" />
          <ColorRow label="Body text" value={design.text_color} onChange={(v) => set({ text_color: v })} testid="color-text" />
          <ColorRow label="Button" value={design.button_bg} onChange={(v) => set({ button_bg: v })} testid="color-btn" />
          <ColorRow label="Button text" value={design.button_text_color} onChange={(v) => set({ button_text_color: v })} testid="color-btntext" />
          <ColorRow label="Link" value={design.link_color} onChange={(v) => set({ link_color: v })} testid="color-link" />
        </div>
        <button type="button" onClick={onResetBrand} data-testid="reset-brand-colors" className="mt-3 text-[12px] font-semibold text-[#1D4E89] hover:text-[#0A2342]">Reset to brand colours</button>
      </div>

      <div>
        <div className="dms-label mb-3">Typography</div>
        <label className="block mb-2">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#64748B]">Heading font</span>
          <select value={design.heading_font} onChange={(e) => set({ heading_font: e.target.value })} data-testid="font-heading" className="mt-1 w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-[13.5px] text-[#0A2342]">
            {fonts.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#64748B]">Body font</span>
          <select value={design.body_font} onChange={(e) => set({ body_font: e.target.value })} data-testid="font-body" className="mt-1 w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-[13.5px] text-[#0A2342]">
            {fonts.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
        {(fontUnsafe(design.heading_font) || fontUnsafe(design.body_font)) && (
          <p className="mt-2 text-[11.5px] text-[#92400E] bg-[#FEFCE8] border border-[#FCD34D] rounded-md px-2.5 py-2" data-testid="font-warning">
            One of these fonts isn&apos;t email-safe. A fallback stack is added automatically, but rendering may vary across email clients.
          </p>
        )}
      </div>

      <div>
        <div className="dms-label mb-3">Layout</div>
        <label className="block mb-3">
          <span className="text-[12.5px] text-[#334155]">Corner radius: {design.radius}px</span>
          <input type="range" min={0} max={28} value={design.radius || 0} onChange={(e) => set({ radius: Number(e.target.value) })} className="w-full" data-testid="layout-radius" />
        </label>
        <label className="block mb-3">
          <span className="text-[12.5px] text-[#334155]">Button radius: {design.button_radius}px</span>
          <input type="range" min={0} max={999} value={design.button_radius || 0} onChange={(e) => set({ button_radius: Number(e.target.value) })} className="w-full" data-testid="layout-btn-radius" />
        </label>
        {mode === "web" && (
          <label className="block mb-1">
            <span className="text-[12.5px] text-[#334155]">Content width: {design.content_width}px</span>
            <input type="range" min={720} max={1280} step={20} value={design.content_width || 1120} onChange={(e) => set({ content_width: Number(e.target.value) })} className="w-full" data-testid="layout-width" />
          </label>
        )}
      </div>

      <button type="button" onClick={onSaveToBrand} data-testid="save-to-brand" className="w-full dms-btn-ghost text-[13px] justify-center">
        Save these colours to Brand Profile
      </button>
    </div>
  );
}
